/* ---------------------------------------------------------------------------
   Teffilah Zmanim — the day-dependent tefilla rules of a Zmanim_N board
   ---------------------------------------------------------------------------
   A Zmanim_N board lists clock times. A *teffilah zman* is the other half of a
   shul board: not a time, but a statement about today's davening — "משיב הרוח
   ומוריד הגשם", "יעלה ויבא", "אין אומרים תחנון", "ספירת העומר ל״ג".

   Everything here is derived from kosher-zmanim's `JewishCalendar`, the same
   package the timed zmanim come from. The version pinned in this repo
   (kosher-zmanim 0.9.0) ships `JewishCalendar` but NOT KosherJava's newer
   `TefilaRules` / `CustomTefillaRules` helper classes, so the rules below are
   implemented here on top of the calendar primitives it does expose
   (`getYomTovIndex`, `isRoshChodesh`, `isTaanis`, `getDayOfOmer`, …). Where a
   rule exists in KosherJava the logic mirrors it; where it does not (Tachanun,
   LeDovid, Aneinu, Sim Shalom, Nachem) the halachic day list is spelled out
   explicitly in the rule so it can be read and adjusted.

   ONE module, used by every layer, so the editor cards, the live preview and
   the display can never disagree:
     - `src/views/Zmanm_N/{Add,Edit,Duplicate}_Zmanim_N.vue` — the Teffilah
       Zmanim tab and its live preview.
     - `src/views/Display.vue` — the rendered board (and, through it, the
       Dashboard screen preview, which embeds Display).

   THE TEFILLA DAY IS NOT THE CIVIL DAY. A Jewish day turns over in the evening,
   so from Maariv onwards the board must already show TOMORROW's tefillos (this
   is how "ותן טל ומטר" starts at Maariv on December 4th). `getTefillahDate`
   does that roll-over against a boundary the board picks: sunset (default —
   Maariv follows sunset), nightfall/tzais, or plain civil midnight.
   -------------------------------------------------------------------------- */

// ----------------------------------------------------------------- rule model

export type TefillahCategoryKey =
  | 'seasonal'
  | 'insertion'
  | 'hallelTachanun'
  | 'calendarCycle'
  | 'holidays'
  | 'omer';

/** How a rule's value reads when the board is asked to print a status. */
export type TefillahStatusStyle =
  /** A yes/no statement about davening ("we say it" / "we do not"). */
  | 'saying'
  /** A property of the day (Rosh Chodesh, Yom Tov, …). */
  | 'state'
  /** A number — Sefiras HaOmer. */
  | 'count';

export interface TefillahRule {
  /** Stable rule id, e.g. `mashivHaruach`. */
  key: string;
  /** Row / override key on a board: `tefillah:<key>`. Never collides with a zman key. */
  itemKey: string;
  category: TefillahCategoryKey;
  /** Hebrew label shown on the board (an org can rename it — see the override table). */
  label: string;
  englishLabel: string;
  /** Plain-English rule, shown in the editor card so the operator sees what it computes. */
  description: string;
  statusStyle: TefillahStatusStyle;
}

export const TEFILLAH_ITEM_KEY_PREFIX = 'tefillah:';

export const TEFILLAH_CATEGORIES: { key: TefillahCategoryKey; label: string }[] = [
  { key: 'seasonal', label: 'Seasonal Prayer' },
  { key: 'insertion', label: 'Prayer Insertion' },
  { key: 'hallelTachanun', label: 'Hallel / Tachanun' },
  { key: 'calendarCycle', label: 'Calendar Cycle' },
  { key: 'holidays', label: 'Holidays & Fasts' },
  { key: 'omer', label: 'Omer Counting' },
];

export const TEFILLAH_RULES: TefillahRule[] = [
  {
    key: 'mashivHaruach',
    category: 'seasonal',
    label: 'משיב הרוח ומוריד הגשם',
    englishLabel: 'Mashiv HaRuach (Rain)',
    description: 'Said in Atah Gibor from Musaf of Shmini Atzeres through Musaf of the first day of Pesach. The two switch-over days themselves read as Morid HaTal.',
    statusStyle: 'saying',
  },
  {
    key: 'moridHatal',
    category: 'seasonal',
    label: 'מוריד הטל',
    englishLabel: 'Morid HaTal (Dew)',
    description: 'Said in Atah Gibor through the summer — every day Mashiv HaRuach is not said.',
    statusStyle: 'saying',
  },
  {
    key: 'veseinTalUmatar',
    category: 'seasonal',
    label: 'ותן טל ומטר לברכה',
    englishLabel: "V'sein Tal U'matar (Rain)",
    description: 'Said in Barech Aleinu through the rainy season — from 7 Cheshvan in Israel, from the evening of Dec 4/5 elsewhere, until Pesach.',
    statusStyle: 'saying',
  },
  {
    key: 'veseinBeracha',
    category: 'seasonal',
    label: 'ותן ברכה',
    englishLabel: "V'sein Bracha (Dew)",
    description: "Said in Barech Aleinu through the summer — every day V'sein Tal U'matar is not said.",
    statusStyle: 'saying',
  },
  {
    key: 'leDovid',
    category: 'seasonal',
    label: 'לדוד ה׳ אורי וישעי',
    englishLabel: 'Psalm 27 (LeDavid)',
    description: 'Said twice daily from 1 Elul through 21 Tishrei (Hoshana Rabba).',
    statusStyle: 'saying',
  },
  {
    key: 'yaalehVeyavo',
    category: 'insertion',
    label: 'יעלה ויבא',
    englishLabel: "Ya'aleh V'Yavo",
    description: 'Inserted in Shemoneh Esrei and Birkas Hamazon on Rosh Chodesh, Yom Tov and Chol HaMoed.',
    statusStyle: 'saying',
  },
  {
    key: 'alHanissim',
    category: 'insertion',
    label: 'על הניסים',
    englishLabel: 'Al HaNissim',
    description: 'Inserted in Shemoneh Esrei and Birkas Hamazon on Chanukah and Purim.',
    statusStyle: 'saying',
  },
  {
    key: 'aneinu',
    category: 'insertion',
    label: 'עננו',
    englishLabel: 'Aneinu',
    description: 'Inserted in Shemoneh Esrei on a public fast day (Yom Kippur excluded).',
    statusStyle: 'saying',
  },
  {
    key: 'simShalom',
    category: 'insertion',
    label: 'שים שלום',
    englishLabel: 'Sim Shalom',
    description: 'Said at Mincha/Maariv in place of Shalom Rav on a day Tachanun or Hallel is said.',
    statusStyle: 'saying',
  },
  {
    key: 'nachem',
    category: 'insertion',
    label: 'נחם',
    englishLabel: 'Nachem',
    description: "Inserted at Mincha of Tisha B'Av.",
    statusStyle: 'saying',
  },
  {
    key: 'tachanun',
    category: 'hallelTachanun',
    label: 'אומרים תחנון',
    englishLabel: 'Tachanun Recited',
    description: 'Tachanun is said at Shacharis today. Pair it with a second card set to "only when it does not apply", renamed "אין אומרים תחנון".',
    statusStyle: 'saying',
  },
  {
    key: 'hallel',
    category: 'hallelTachanun',
    label: 'הלל',
    englishLabel: 'Hallel Recited',
    description: 'Any Hallel — full or half — is said today.',
    statusStyle: 'saying',
  },
  {
    key: 'hallelShalem',
    category: 'hallelTachanun',
    label: 'הלל שלם',
    englishLabel: 'Full Hallel',
    description: 'Full Hallel: Chanukah, Succos through Simchas Torah, Shavuos, and the first day(s) of Pesach.',
    statusStyle: 'saying',
  },
  {
    key: 'chatziHallel',
    category: 'hallelTachanun',
    label: 'חצי הלל',
    englishLabel: 'Half Hallel',
    description: 'Half Hallel: Rosh Chodesh, and Chol HaMoed / the last days of Pesach.',
    statusStyle: 'saying',
  },
  {
    key: 'roshChodesh',
    category: 'calendarCycle',
    label: 'ראש חודש',
    englishLabel: 'Rosh Chodesh',
    description: 'The new month — the 1st, and the 30th of a 30-day month.',
    statusStyle: 'state',
  },
  {
    key: 'erevRoshChodesh',
    category: 'calendarCycle',
    label: 'ערב ראש חודש',
    englishLabel: 'Erev Rosh Chodesh',
    description: 'The day before Rosh Chodesh (Yom Kippur Katan). Erev Rosh Hashana is excluded.',
    statusStyle: 'state',
  },
  {
    key: 'shabbosMevorchim',
    category: 'calendarCycle',
    label: 'שבת מברכים',
    englishLabel: 'Shabbos Mevorchim',
    description: 'The Shabbos before Rosh Chodesh, when the new month is blessed (never in Elul).',
    statusStyle: 'state',
  },
  {
    key: 'shabbosRoshChodesh',
    category: 'calendarCycle',
    label: 'שבת וראש חודש',
    englishLabel: 'Shabbos Rosh Chodesh',
    description: 'Rosh Chodesh falling on Shabbos — a special Haftarah.',
    statusStyle: 'state',
  },
  {
    key: 'macharChodesh',
    category: 'calendarCycle',
    label: 'מחר חודש',
    englishLabel: 'Machar Chodesh',
    description: 'Shabbos immediately before Rosh Chodesh — the Machar Chodesh Haftarah.',
    statusStyle: 'state',
  },
  {
    key: 'yomTov',
    category: 'holidays',
    label: 'יום טוב',
    englishLabel: 'Yom Tov',
    description: 'A festival day: Pesach, Shavuos, Succos, Shemini Atzeres, Rosh Hashana, Yom Kippur.',
    statusStyle: 'state',
  },
  {
    key: 'erevYomTov',
    category: 'holidays',
    label: 'ערב יום טוב',
    englishLabel: 'Erev Yom Tov',
    description: 'The day before a festival (Hoshana Rabba included).',
    statusStyle: 'state',
  },
  {
    key: 'cholHamoed',
    category: 'holidays',
    label: 'חול המועד',
    englishLabel: 'Chol HaMoed',
    description: 'The intermediate days of Pesach and Succos.',
    statusStyle: 'state',
  },
  {
    key: 'chanukah',
    category: 'holidays',
    label: 'חנוכה',
    englishLabel: 'Chanukah',
    description: 'The eight days of Chanukah.',
    statusStyle: 'state',
  },
  {
    key: 'purim',
    category: 'holidays',
    label: 'פורים',
    englishLabel: 'Purim',
    description: '14 Adar (14 Adar II in a leap year).',
    statusStyle: 'state',
  },
  {
    key: 'shushanPurim',
    category: 'holidays',
    label: 'שושן פורים',
    englishLabel: 'Shushan Purim',
    description: '15 Adar — Purim in a walled city.',
    statusStyle: 'state',
  },
  {
    key: 'purimKatan',
    category: 'holidays',
    label: 'פורים קטן',
    englishLabel: 'Purim Katan',
    description: '14 Adar I in a leap year.',
    statusStyle: 'state',
  },
  {
    key: 'taanis',
    category: 'holidays',
    label: 'תענית',
    englishLabel: 'Public Fast Day',
    description: "17 Tammuz, 9 Av, Tzom Gedalia, 10 Teves, Taanis Esther, Yom Kippur — moved when they fall on Shabbos.",
    statusStyle: 'state',
  },
  {
    key: 'omer',
    category: 'omer',
    label: 'ספירת העומר',
    englishLabel: 'Day of the Omer',
    description: 'Days 1–49 of the Omer, counted in Hebrew numerals. Blank outside the Sefira.',
    statusStyle: 'count',
  },
].map((rule) => ({ ...rule, itemKey: `${TEFILLAH_ITEM_KEY_PREFIX}${rule.key}` })) as TefillahRule[];

export const TEFILLAH_RULE_BY_KEY: Record<string, TefillahRule> = TEFILLAH_RULES.reduce(
  (map, rule) => {
    map[rule.key] = rule;
    return map;
  },
  {} as Record<string, TefillahRule>,
);

export const TEFILLAH_RULE_BY_ITEM_KEY: Record<string, TefillahRule> = TEFILLAH_RULES.reduce(
  (map, rule) => {
    map[rule.itemKey] = rule;
    return map;
  },
  {} as Record<string, TefillahRule>,
);

/** Canonical position of a rule, so a board's rows are ordered predictably. */
const TEFILLAH_RULE_ORDER: Record<string, number> = TEFILLAH_RULES.reduce(
  (map, rule, index) => {
    map[rule.key] = index;
    return map;
  },
  {} as Record<string, number>,
);

export const isTefillahItemKey = (key: unknown): boolean =>
  typeof key === 'string' && key.startsWith(TEFILLAH_ITEM_KEY_PREFIX);

export const tefillahKeyFromItemKey = (itemKey: string): string =>
  String(itemKey || '').slice(TEFILLAH_ITEM_KEY_PREFIX.length);

// -------------------------------------------------------------- board config

/** When the board stops showing today's tefillos and starts showing tomorrow's. */
export type TefillahDayBoundary = 'sunset' | 'tzais' | 'midnight';

/** Diaspora vs Israel changes Yom Tov Sheni, Hallel and the Tal U'matar start. */
export type TefillahRegion = 'auto' | 'israel' | 'diaspora';

/** Whether a selected rule's row is on the board today. */
export type TefillahVisibility =
  /** Only on days the rule applies (the default, and what most boards want). */
  | 'active'
  /** Every day — pair with a status text so the row still says something. */
  | 'always'
  /** Only on days the rule does NOT apply (e.g. a renamed "אין אומרים תחנון"). */
  | 'inactive';

/** What goes in the row's value slot (where a timed zman prints its clock time). */
export type TefillahStatusTextMode = 'none' | 'yesNo' | 'saying';

export interface TefillahItemConfig {
  visibility: TefillahVisibility;
  statusText: TefillahStatusTextMode;
}

export interface TefillahDesign {
  dayBoundary: TefillahDayBoundary;
  region: TefillahRegion;
  /** Where the tefillah rows sit relative to the timed zmanim. */
  position: 'before' | 'after';
  selected: string[];
  items: Record<string, TefillahItemConfig>;
}

export const DEFAULT_TEFILLAH_ITEM_CONFIG: TefillahItemConfig = {
  visibility: 'active',
  statusText: 'none',
};

export const TEFILLAH_DAY_BOUNDARY_OPTIONS: { value: TefillahDayBoundary; label: string }[] = [
  { value: 'sunset', label: 'Sunset (שקיעה)' },
  { value: 'tzais', label: 'Nightfall (צאת הכוכבים)' },
  { value: 'midnight', label: 'Civil midnight' },
];

export const TEFILLAH_REGION_OPTIONS: { value: TefillahRegion; label: string }[] = [
  { value: 'auto', label: 'Auto (from timezone)' },
  { value: 'israel', label: 'Israel' },
  { value: 'diaspora', label: 'Outside Israel' },
];

export const TEFILLAH_POSITION_OPTIONS: { value: 'before' | 'after'; label: string }[] = [
  { value: 'after', label: 'After the zmanim' },
  { value: 'before', label: 'Before the zmanim' },
];

export const TEFILLAH_VISIBILITY_OPTIONS: { value: TefillahVisibility; label: string }[] = [
  { value: 'active', label: 'Only when it applies' },
  { value: 'always', label: 'Every day' },
  { value: 'inactive', label: 'Only when it does not apply' },
];

export const TEFILLAH_STATUS_TEXT_OPTIONS: { value: TefillahStatusTextMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'yesNo', label: 'כן / לא' },
  { value: 'saying', label: 'אומרים / אין אומרים' },
];

const STATUS_TEXTS: Record<Exclude<TefillahStatusTextMode, 'none'>, { yes: string; no: string }> = {
  yesNo: { yes: 'כן', no: 'לא' },
  saying: { yes: 'אומרים', no: 'אין אומרים' },
};

export function createDefaultTefillahDesign(): TefillahDesign {
  return {
    dayBoundary: 'sunset',
    region: 'auto',
    position: 'after',
    selected: [],
    items: {},
  };
}

const asOneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  (allowed as readonly string[]).includes(String(value)) ? (String(value) as T) : fallback;

export function normalizeTefillahItemConfig(raw: any): TefillahItemConfig {
  return {
    visibility: asOneOf(raw?.visibility, ['active', 'always', 'inactive'] as const, 'active'),
    statusText: asOneOf(raw?.statusText, ['none', 'yesNo', 'saying'] as const, 'none'),
  };
}

/**
 * Read a stored `design.tefillah` back into a complete, valid config: unknown
 * rule keys are dropped (so a renamed/removed rule cannot break a board), the
 * selection is de-duplicated and put back into canonical rule order, and every
 * selected rule is guaranteed an item config.
 */
export function normalizeTefillahDesign(raw: any): TefillahDesign {
  const base = createDefaultTefillahDesign();
  if (!raw || typeof raw !== 'object') return base;

  const seen = new Set<string>();
  const selected = (Array.isArray(raw.selected) ? raw.selected : [])
    .map((key: unknown) => String(key || ''))
    .filter((key: string) => {
      if (!TEFILLAH_RULE_BY_KEY[key] || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a: string, b: string) => TEFILLAH_RULE_ORDER[a] - TEFILLAH_RULE_ORDER[b]);

  // Item settings survive a rule being switched off and back on, so an
  // accidental toggle does not silently reset how the row reads. Only known
  // rule keys are kept, which bounds the stored map at one entry per rule.
  const items: Record<string, TefillahItemConfig> = {};
  Object.keys(raw.items && typeof raw.items === 'object' ? raw.items : {}).forEach((key) => {
    if (TEFILLAH_RULE_BY_KEY[key]) items[key] = normalizeTefillahItemConfig(raw.items[key]);
  });
  selected.forEach((key: string) => {
    if (!items[key]) items[key] = { ...DEFAULT_TEFILLAH_ITEM_CONFIG };
  });

  return {
    dayBoundary: asOneOf(raw.dayBoundary, ['sunset', 'tzais', 'midnight'] as const, base.dayBoundary),
    region: asOneOf(raw.region, ['auto', 'israel', 'diaspora'] as const, base.region),
    position: asOneOf(raw.position, ['before', 'after'] as const, base.position),
    selected,
    items,
  };
}

/** Israel timezones — the basis of the `auto` region setting. */
const ISRAEL_TIMEZONES = new Set(['asia/jerusalem', 'asia/tel_aviv', 'israel']);

export function resolveTefillahInIsrael(region: TefillahRegion, timezone?: string | null): boolean {
  if (region === 'israel') return true;
  if (region === 'diaspora') return false;
  return ISRAEL_TIMEZONES.has(String(timezone || '').trim().toLowerCase());
}

// ------------------------------------------------------------ the tefilla day

/**
 * The civil date whose Jewish day the board should be showing.
 *
 * `sunsetIso` / `tzaisIso` must be TODAY's (i.e. `now`'s own civil date)
 * values; once the clock passes the chosen boundary the Jewish day has turned
 * over and the board moves on to tomorrow. Missing boundary values fall back to
 * civil midnight rather than guessing, so a board never shows the wrong day.
 *
 * `civilDate` is `now`'s civil date when it has been read somewhere other than
 * this device's own timezone (the display reads it on the screen's clock); the
 * day is counted from it, while `now` is still what meets the boundary.
 */
export function getTefillahDate(
  now: Date,
  options: { boundary?: TefillahDayBoundary; sunsetIso?: string; tzaisIso?: string; civilDate?: Date | null } = {},
): Date {
  const base = now instanceof Date && !Number.isNaN(now.getTime()) ? new Date(now.getTime()) : new Date();
  const { civilDate } = options;
  const day = civilDate instanceof Date && !Number.isNaN(civilDate.getTime()) ? new Date(civilDate.getTime()) : base;
  const boundary = options.boundary || 'sunset';
  if (boundary === 'midnight') return day;

  const iso = boundary === 'tzais' ? options.tzaisIso : options.sunsetIso;
  const boundaryMs = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(boundaryMs) || base.getTime() < boundaryMs) return day;

  const next = new Date(day.getTime());
  next.setDate(next.getDate() + 1);
  return next;
}

/** `YYYY-MM-DD` of a civil date — the cache key for one day's evaluation. */
export function getTefillahDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// -------------------------------------------------------------- the rules

/* Jewish months are numbered from Nissan in kosher-zmanim. */
const NISSAN = 1;
const IYAR = 2;
const SIVAN = 3;
const AV = 5;
const ELUL = 6;
const TISHREI = 7;
const SHEVAT = 11;
const ADAR = 12;
const ADAR_II = 13;

const SATURDAY = 7; // kosher-zmanim getDayOfWeek(): 1 = Sunday … 7 = Saturday

/** Absolute date of 1 Tishrei year 1 — JewishDate.JEWISH_EPOCH in kosher-zmanim. */
const JEWISH_EPOCH = -1373429;

const safeBool = (fn: () => unknown): boolean => {
  try {
    return Boolean(fn());
  } catch (error) {
    return false;
  }
};

const safeNumber = (fn: () => unknown, fallback: number): number => {
  try {
    const value = Number(fn());
    return Number.isFinite(value) ? value : fallback;
  } catch (error) {
    return fallback;
  }
};

/**
 * Days elapsed since the first Tekufas Tishrei — KosherJava's
 * `getTekufasTishreiElapsedDays()`, which this build of kosher-zmanim does not
 * expose. It is what fixes the Diaspora start of ותן טל ומטר to the evening of
 * December 4th (5th in the year before a secular leap year, and drifting a
 * further day each Gregorian century): the count reaches 47 exactly then.
 *
 * Rebuilt from `getAbsDate()`, which kosher-zmanim defines as
 * `daysSinceStartOfJewishYear + jewishCalendarElapsedDays(year) + JEWISH_EPOCH`
 * — so `absDate - JEWISH_EPOCH - 1` is KosherJava's
 * `getJewishCalendarElapsedDays(year) + (getDaysSinceStartOfJewishYear() - 1)`.
 * The extra half day is the 9 hours the first tekufa fell into its day, which
 * lets all four years of the leap cycle share the same 47.
 */
function getTekufasTishreiElapsedDays(calendar: any): number {
  const absDate = safeNumber(() => calendar.getAbsDate(), NaN);
  const jewishYear = safeNumber(() => calendar.getJewishYear(), NaN);
  if (!Number.isFinite(absDate) || !Number.isFinite(jewishYear)) return 0;
  const days = absDate - JEWISH_EPOCH - 1 + 0.5;
  const solar = (jewishYear - 1) * 365.25;
  return Math.floor(days - solar);
}

interface RuleInput {
  calendar: any;
  JewishCalendar: any;
  JewishDate: any;
  inIsrael: boolean;
  month: number;
  day: number;
  dayOfWeek: number;
  yomTovIndex: number;
  isLeapYear: boolean;
  /** The Adar that carries Purim — Adar in a plain year, Adar II in a leap year. */
  purimAdar: number;
}

const compareToJewishDate = (input: RuleInput, month: number, day: number): number => {
  const { calendar, JewishDate } = input;
  try {
    return calendar.compareTo(new JewishDate(calendar.getJewishYear(), month, day));
  } catch (error) {
    return 0;
  }
};

/**
 * משיב הרוח ומוריד הגשם — from Musaf of Shmini Atzeres (22 Tishrei) until
 * Musaf of the first day of Pesach (15 Nissan). Both switch-over days are
 * excluded, matching KosherJava's `isMashivHaruachRecited`: on those two days
 * the nusach changes mid-morning, and Morid HaTal covers them.
 */
const isMashivHaruach = (input: RuleInput): boolean =>
  compareToJewishDate(input, TISHREI, 22) > 0 && compareToJewishDate(input, NISSAN, 15) < 0;

/**
 * ותן טל ומטר לברכה — in Israel from 7 Cheshvan, elsewhere from the 47th day
 * after Tekufas Tishrei (the evening of Dec 4/5), through 14 Nissan.
 * Mirrors KosherJava's `isVeseinTalUmatarRecited`.
 */
function isVeseinTalUmatar(input: RuleInput): boolean {
  const { month, day, inIsrael } = input;
  if (month === NISSAN && day < 15) return true;
  // Months are numbered from Nissan, so anything before Cheshvan is Nissan
  // 15 → Tishrei 30: the dry half of the year.
  if (month < 8) return false;
  if (inIsrael) return month !== 8 || day >= 7;
  return getTekufasTishreiElapsedDays(input.calendar) >= 47;
}

/**
 * אומרים תחנון (Shacharis). Tachanun is said on an ordinary weekday; the days
 * it is omitted are listed here in calendar order.
 */
function isTachanunRecited(input: RuleInput): boolean {
  const { calendar, JewishCalendar, month, day, dayOfWeek, yomTovIndex, isLeapYear, purimAdar } = input;

  if (dayOfWeek === SATURDAY) return false;
  if (safeBool(() => calendar.isRoshChodesh())) return false;
  // Yom Tov, Chol HaMoed and Isru Chag — all of Nissan / Sivan 1-12 /
  // Tishrei 9-30 below already cover most of these, but Rosh Hashana and a
  // Yom Tov reached by any other path are caught here.
  if (safeBool(() => calendar.isYomTov())) return false;
  if (safeBool(() => calendar.isCholHamoed())) return false;
  if (safeBool(() => calendar.isIsruChag())) return false;

  // The whole month of Nissan.
  if (month === NISSAN) return false;
  // Pesach Sheni (14 Iyar) and Lag BaOmer (18 Iyar).
  if (month === IYAR && (day === 14 || day === 18)) return false;
  // Through 12 Sivan — Shavuos and the week of tashlumin that follows it.
  if (month === SIVAN && day <= 12) return false;
  // Tisha B'Av (pushed off to the 10th when the 9th is Shabbos) and Tu B'Av.
  if (yomTovIndex === JewishCalendar.TISHA_BEAV) return false;
  if (month === AV && day === 15) return false;
  // Erev Rosh Hashana.
  if (month === ELUL && day === 29) return false;
  // Rosh Hashana (1-2) and, from Erev Yom Kippur, the rest of Tishrei.
  if (month === TISHREI && (day <= 2 || day >= 9)) return false;
  // Chanukah — 25 Kislev through the 2nd or 3rd of Teves, depending on whether
  // Kislev is short that year. `isChanukah()` already accounts for that.
  if (safeBool(() => calendar.isChanukah())) return false;
  // Tu BiShvat.
  if (month === SHEVAT && day === 15) return false;
  // Purim Katan / Shushan Purim Katan — 14 & 15 of Adar I in a leap year.
  if (isLeapYear && month === ADAR && (day === 14 || day === 15)) return false;
  // Purim and Shushan Purim.
  if (month === purimAdar && (day === 14 || day === 15)) return false;

  return true;
}

/**
 * הלל — any Hallel, full or half. Mirrors KosherJava's `isHallelRecited`,
 * without the modern Israeli holidays.
 */
function isHallelRecited(input: RuleInput): boolean {
  const { calendar, month, day, inIsrael } = input;
  if (safeBool(() => calendar.isRoshChodesh())) return true; // Rosh Hashana returns false
  if (safeBool(() => calendar.isChanukah())) return true;
  if (month === NISSAN) return day >= 15 && (inIsrael ? day <= 21 : day <= 22);
  if (month === SIVAN) return day === 6 || (!inIsrael && day === 7);
  if (month === TISHREI) return day >= 15 && (day <= 22 || (!inIsrael && day <= 23));
  return false;
}

/**
 * הלל שלם — Hallel is said in full except on Rosh Chodesh (unless that Rosh
 * Chodesh is Chanukah) and from the second/third day of Pesach onward.
 * Mirrors KosherJava's `isHallelShalemRecited`.
 */
function isHallelShalemRecited(input: RuleInput): boolean {
  const { calendar, month, day, inIsrael } = input;
  if (!isHallelRecited(input)) return false;
  const isChanukah = safeBool(() => calendar.isChanukah());
  if (safeBool(() => calendar.isRoshChodesh()) && !isChanukah) return false;
  if (month === NISSAN && (inIsrael ? day > 15 : day > 16)) return false;
  return true;
}

/** יעלה ויבא — Rosh Chodesh, Yom Tov, Chol HaMoed and Hoshana Rabba. */
function isYaalehVeyavo(input: RuleInput): boolean {
  const { calendar, JewishCalendar, yomTovIndex } = input;
  if (safeBool(() => calendar.isRoshChodesh())) return true;
  if (safeBool(() => calendar.isCholHamoed())) return true;
  return [
    JewishCalendar.PESACH,
    JewishCalendar.SHAVUOS,
    JewishCalendar.ROSH_HASHANA,
    JewishCalendar.YOM_KIPPUR,
    JewishCalendar.SUCCOS,
    JewishCalendar.HOSHANA_RABBA,
    JewishCalendar.SHEMINI_ATZERES,
    JewishCalendar.SIMCHAS_TORAH,
  ].includes(yomTovIndex);
}

type RuleEvaluator = (input: RuleInput) => boolean;

const RULE_EVALUATORS: Record<string, RuleEvaluator> = {
  mashivHaruach: isMashivHaruach,
  moridHatal: (input) => !isMashivHaruach(input),
  veseinTalUmatar: isVeseinTalUmatar,
  veseinBeracha: (input) => !isVeseinTalUmatar(input),
  // לדוד ה׳ אורי — 1 Elul through Hoshana Rabba (21 Tishrei).
  leDovid: ({ month, day }) => month === ELUL || (month === TISHREI && day <= 21),

  yaalehVeyavo: isYaalehVeyavo,
  alHanissim: ({ calendar }) =>
    safeBool(() => calendar.isChanukah()) || safeBool(() => calendar.isPurim()),
  // עננו is said on every public fast — but not on Yom Kippur, whose Shemoneh
  // Esrei has its own text.
  aneinu: ({ calendar, JewishCalendar, yomTovIndex }) =>
    safeBool(() => calendar.isTaanis()) && yomTovIndex !== JewishCalendar.YOM_KIPPUR,
  simShalom: (input) => isTachanunRecited(input) || isHallelRecited(input),
  nachem: ({ JewishCalendar, yomTovIndex }) => yomTovIndex === JewishCalendar.TISHA_BEAV,

  tachanun: isTachanunRecited,
  hallel: isHallelRecited,
  hallelShalem: isHallelShalemRecited,
  chatziHallel: (input) => isHallelRecited(input) && !isHallelShalemRecited(input),

  roshChodesh: ({ calendar }) => safeBool(() => calendar.isRoshChodesh()),
  erevRoshChodesh: ({ calendar }) => safeBool(() => calendar.isErevRoshChodesh()),
  shabbosMevorchim: ({ calendar }) => safeBool(() => calendar.isShabbosMevorchim()),
  shabbosRoshChodesh: ({ calendar, dayOfWeek }) =>
    dayOfWeek === SATURDAY && safeBool(() => calendar.isRoshChodesh()),
  // Machar Chodesh is the Shabbos whose tomorrow is Rosh Chodesh — always the
  // 29th. kosher-zmanim's isMacharChodesh() also answers true on a 30th that
  // falls on Shabbos, but that day is itself Rosh Chodesh and takes the
  // Shabbos-Rosh-Chodesh haftarah; 29 Elul leads into Rosh Hashana, not a
  // Rosh Chodesh, so it is excluded too.
  macharChodesh: ({ month, day, dayOfWeek }) => dayOfWeek === SATURDAY && day === 29 && month !== ELUL,

  // "Yom Tov" on a board means a festival day proper — Chol HaMoed, Chanukah,
  // Purim and the other minor days that kosher-zmanim's broad isYomTov() also
  // covers get their own cards.
  yomTov: ({ calendar }) => safeBool(() => calendar.isYomTovAssurBemelacha()),
  erevYomTov: ({ calendar }) => safeBool(() => calendar.isErevYomTov()),
  cholHamoed: ({ calendar }) => safeBool(() => calendar.isCholHamoed()),
  chanukah: ({ calendar }) => safeBool(() => calendar.isChanukah()),
  purim: ({ JewishCalendar, yomTovIndex }) => yomTovIndex === JewishCalendar.PURIM,
  shushanPurim: ({ JewishCalendar, yomTovIndex }) => yomTovIndex === JewishCalendar.SHUSHAN_PURIM,
  purimKatan: ({ JewishCalendar, yomTovIndex }) => yomTovIndex === JewishCalendar.PURIM_KATAN,
  taanis: ({ calendar }) => safeBool(() => calendar.isTaanis()),

  omer: ({ calendar }) => safeNumber(() => calendar.getDayOfOmer(), -1) > 0,
};

export interface TefillahStatus {
  /** Does the rule apply to this Jewish day? */
  active: boolean;
  /** The rule's own value — the Omer count, or '' for a yes/no rule. */
  value: string;
}

export type TefillahEvaluation = Record<string, TefillahStatus>;

export interface TefillahCalendarModule {
  JewishCalendar: any;
  JewishDate: any;
  HebrewDateFormatter?: any;
}

/** Load the kosher-zmanim classes the rules need (the package is code-split). */
export async function loadTefillahCalendarModule(): Promise<TefillahCalendarModule | null> {
  try {
    const kz: any = await import('kosher-zmanim');
    if (!kz?.JewishCalendar || !kz?.JewishDate) return null;
    return {
      JewishCalendar: kz.JewishCalendar,
      JewishDate: kz.JewishDate,
      HebrewDateFormatter: kz.HebrewDateFormatter,
    };
  } catch (error) {
    return null;
  }
}

/** ל״ג, י״ז … the Omer count in Hebrew numerals. */
function formatHebrewCount(module: TefillahCalendarModule, count: number): string {
  const Formatter = module.HebrewDateFormatter;
  if (Formatter) {
    try {
      const formatter = new Formatter();
      if (typeof formatter.setHebrewFormat === 'function') formatter.setHebrewFormat(true);
      const formatted = formatter.formatHebrewNumber(count);
      if (formatted) return String(formatted);
    } catch (error) {
      /* fall through to the plain number */
    }
  }
  return String(count);
}

/**
 * Evaluate every rule for one civil date. The date is read as a Jewish day —
 * the caller has already rolled it over at the board's evening boundary.
 */
export function evaluateTefillahRules(
  module: TefillahCalendarModule | null,
  date: Date,
  options: { inIsrael?: boolean } = {},
): TefillahEvaluation {
  const evaluation: TefillahEvaluation = {};
  if (!module?.JewishCalendar || !module?.JewishDate) return evaluation;

  const { JewishCalendar, JewishDate } = module;
  const inIsrael = Boolean(options.inIsrael);

  let calendar: any;
  try {
    // Noon keeps the calendar's Y/M/D reading clear of DST and midnight edges;
    // JewishCalendar only reads the civil date, never the time of day.
    const safeDate = new Date(date.getTime());
    safeDate.setHours(12, 0, 0, 0);
    calendar = new JewishCalendar(safeDate);
    // Yom Haatzmaut / Yom Yerushalayim must not turn into a Yom Tov here — they
    // would suppress Tachanun and add Hallel for every organization.
    if (typeof calendar.setUseModernHolidays === 'function') calendar.setUseModernHolidays(false);
    if (typeof calendar.setInIsrael === 'function') calendar.setInIsrael(inIsrael);
  } catch (error) {
    return evaluation;
  }

  const isLeapYear = safeBool(() => calendar.isJewishLeapYear());
  const input: RuleInput = {
    calendar,
    JewishCalendar,
    JewishDate,
    inIsrael,
    month: safeNumber(() => calendar.getJewishMonth(), 0),
    day: safeNumber(() => calendar.getJewishDayOfMonth(), 0),
    dayOfWeek: safeNumber(() => calendar.getDayOfWeek(), 0),
    yomTovIndex: safeNumber(() => calendar.getYomTovIndex(), -1),
    isLeapYear,
    purimAdar: isLeapYear ? ADAR_II : ADAR,
  };

  const omerDay = safeNumber(() => calendar.getDayOfOmer(), -1);

  TEFILLAH_RULES.forEach((rule) => {
    const evaluator = RULE_EVALUATORS[rule.key];
    let active = false;
    try {
      active = evaluator ? Boolean(evaluator(input)) : false;
    } catch (error) {
      active = false;
    }
    evaluation[rule.key] = {
      active,
      value: rule.key === 'omer' && omerDay > 0 ? formatHebrewCount(module, omerDay) : '',
    };
  });

  return evaluation;
}

// ------------------------------------------------------------------- rows

export interface TefillahRow {
  /** `tefillah:<key>` — the row key, and the org rename key. */
  key: string;
  label: string;
  /** The row's value slot, where a timed zman prints its clock time. */
  time: string;
  /** Tefillah rows have no clock time; they never sort or count as "upcoming". */
  dateTime: null;
  isTefillah: true;
  tefillahKey: string;
  active: boolean;
}

/**
 * Whether a Zmanim_N row has anything to print in its value slot. Timed zmanim
 * always do; a tefillah row does only when its status-text mode says so
 * ('none', the default, leaves the slot blank). A row without a value renders
 * just its label - no time span and no label/time divider - so the spacing on
 * both sides of the horizontal ticker's separator symbol stays constant, and a
 * vertical list never draws a connector line to nothing.
 */
export function zmanimRowHasTime(row: { time?: string | null } | null | undefined): boolean {
  return String(row?.time ?? '').trim().length > 0;
}

/** The value a row prints for its rule, given the item's status-text mode. */
export function getTefillahStatusText(
  rule: TefillahRule,
  status: TefillahStatus,
  config: TefillahItemConfig,
): string {
  if (rule.statusStyle === 'count') return status.value;
  if (config.statusText === 'none') return '';
  const texts = STATUS_TEXTS[config.statusText];
  return status.active ? texts.yes : texts.no;
}

/** Does a selected rule's row belong on the board today? */
export function isTefillahRowVisible(status: TefillahStatus, config: TefillahItemConfig): boolean {
  if (config.visibility === 'always') return true;
  if (config.visibility === 'inactive') return !status.active;
  return status.active;
}

/**
 * The tefillah rows of a board, in canonical rule order. `resolveLabel` lets
 * each caller apply the organization's rename (Zmanim_Display_Override_N,
 * keyed by the row's `tefillah:<key>`) without this module knowing about it.
 */
export function buildTefillahRows(options: {
  design: TefillahDesign;
  evaluation: TefillahEvaluation;
  resolveLabel?: (itemKey: string, defaultLabel: string) => string;
}): TefillahRow[] {
  const { design, evaluation } = options;
  const resolveLabel = options.resolveLabel || ((_key: string, label: string) => label);
  if (!design?.selected?.length) return [];

  return design.selected.reduce((rows: TefillahRow[], key: string) => {
    const rule = TEFILLAH_RULE_BY_KEY[key];
    if (!rule) return rows;
    const status = evaluation[key];
    if (!status) return rows;
    const config = normalizeTefillahItemConfig(design.items?.[key]);
    if (!isTefillahRowVisible(status, config)) return rows;

    rows.push({
      key: rule.itemKey,
      label: resolveLabel(rule.itemKey, rule.label),
      time: getTefillahStatusText(rule, status, config),
      dateTime: null,
      isTefillah: true,
      tefillahKey: rule.key,
      active: status.active,
    });
    return rows;
  }, []);
}

/** Place the tefillah rows around the board's timed zmanim. */
export function mergeTefillahRows<T>(
  zmanimRows: T[],
  tefillahRows: TefillahRow[],
  position: 'before' | 'after',
): (T | TefillahRow)[] {
  if (!tefillahRows.length) return zmanimRows;
  return position === 'before'
    ? [...tefillahRows, ...zmanimRows]
    : [...zmanimRows, ...tefillahRows];
}
